class VariantSwatches extends HTMLElement {
  constructor() {
    super();
    this.productMediaMap = this.getProductMediaMap();
    this.onChangeBound = this.onChange.bind(this);
    this.pendingImage = null;
    this.loadingOverlay = null;
  }

  connectedCallback() {
    this.addEventListener("change", this.onChangeBound);
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.onChangeBound);
    this.pendingImage = null;
    this.removeLoadingOverlay();
  }

  getProductMediaMap() {
    try {
      const scriptElement = this.querySelector('script[type="application/json"]');
      if (!scriptElement) {
        return new Map();
      }

      const productMedia = JSON.parse(scriptElement.textContent);
      const mediaMap = new Map();

      productMedia.forEach((mediaItem) => {
        if (mediaItem?.id && mediaItem?.src) {
          mediaMap.set(String(mediaItem.id), mediaItem.src);
        }
      });

      return mediaMap;
    } catch (error) {
      console.error("Error parsing product media", error);
      return new Map();
    }
  }

  onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== "radio") {
      return;
    }

    const mediaId = input.dataset.mediaId;
    if (!mediaId) {
      return;
    }

    const mediaSrc = this.productMediaMap.get(String(mediaId));
    if (mediaSrc) {
      this.replaceMedia(mediaSrc);
    }
  }

  getImageElements() {
    const productCardContainer = this.closest(".kc-product-card");
    if (!productCardContainer) {
      return null;
    }

    const imageWrapper = productCardContainer.querySelector(".kc-product-card__image");
    if (!imageWrapper) {
      return null;
    }

    const image = imageWrapper.querySelector("img");
    if (!image) {
      return null;
    }

    return { imageWrapper, image };
  }

  createLoadingOverlay() {
    VariantSwatches.ensureLoadingStyles();

    const overlay = document.createElement("div");
    overlay.className = "variant-swatches-loading-overlay";

    const spinner = document.createElement("div");
    spinner.className = "variant-swatches-loading-spinner";
    overlay.appendChild(spinner);

    return overlay;
  }

  removeLoadingOverlay() {
    if (this.loadingOverlay) {
      this.loadingOverlay.remove();
      this.loadingOverlay = null;
    }
  }

  static ensureLoadingStyles() {
    if (document.getElementById("variant-swatches-loading-styles")) {
      return;
    }

    const style = document.createElement("style");
    style.id = "variant-swatches-loading-styles";
    style.textContent = `
      .variant-swatches-loading-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
      }
      .variant-swatches-loading-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid #f3f3f3;
        border-top: 3px solid #333;
        border-radius: 50%;
        animation: variant-swatches-spin 1s linear infinite;
      }
      @keyframes variant-swatches-spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }

  async replaceMedia(mediaSrc) {
    if (!mediaSrc) return;

    const elements = this.getImageElements();
    if (!elements) return;

    const { imageWrapper, image } = elements;
    if (image.currentSrc === mediaSrc || image.src === mediaSrc) {
      return;
    }

    image.removeAttribute("srcset");
    this.removeLoadingOverlay();
    this.loadingOverlay = this.createLoadingOverlay();
    imageWrapper.appendChild(this.loadingOverlay);

    const preloadImage = new Image();
    this.pendingImage = preloadImage;
    preloadImage.src = mediaSrc;

    try {
      if (typeof preloadImage.decode === "function") {
        await preloadImage.decode();
      } else {
        await new Promise((resolve, reject) => {
          preloadImage.onload = resolve;
          preloadImage.onerror = reject;
        });
      }

      if (this.pendingImage !== preloadImage) {
        return;
      }

      image.src = mediaSrc;
    } catch (error) {
      if (this.pendingImage === preloadImage) {
        console.error("Error loading image:", mediaSrc, error);
      }
    } finally {
      if (this.pendingImage === preloadImage) {
        this.pendingImage = null;
        this.removeLoadingOverlay();
      }
    }
  }
}

if (!customElements.get("variant-swatches")) {
  customElements.define("variant-swatches", VariantSwatches);
}
